/**
 * `pb-ai-connectors`: how a person connects an AI to one of Privacy Bee's two
 * MCP servers, as data. Which AIs the server admits, the steps each AI's own
 * app walks, and the name the connection carries there.
 *
 * Two surfaces render it: hive's Connect your AI dialog for the member server,
 * and the admin console's Admin MCP page for the staff one. Each renders it with
 * its own components, so this package holds words and the strings built from
 * them, never markup. A vendor that renames a menu is corrected here once, for
 * both.
 *
 * Adding or retiring an AI is adding or deleting its entry in `AI_CLIENTS`. An
 * entry belongs here only while app-api admits its client: the server registers
 * no client dynamically and admits exactly the clients `config/mcp.php` names,
 * so a guide for any other AI ends at an `invalid_client` page.
 */
/** The member's own server: hive's Connect your AI. */
export const MEMBER_SERVER = {
    name: "Privacy Bee",
    cliName: "privacy-bee",
    account: "Privacy Bee account",
};
/** The admin console's server: the Admin MCP page. */
export const ADMIN_SERVER = {
    name: "Privacy Bee - Internal Admin Controls",
    cliName: "privacy-bee-admin",
    account: "Privacy Bee staff account",
};
/** A config file's entry for the server, keyed by the connection's identifier, as the person pastes it. */
function configEntry(root, target, server) {
    return JSON.stringify({ [root]: { [target.cliName]: server } }, null, 2);
}
/**
 * Cursor's documented install link: the server's own config object, as JSON,
 * base64-encoded into `config`, and in `name` the key it takes in `mcp.json`,
 * the same key the pasted entry uses.
 */
function cursorInstallLink(target) {
    const config = btoa(JSON.stringify({ url: target.url }));
    return `cursor://anysphere.cursor-deeplink/mcp/install?name=${encodeURIComponent(target.cliName)}&config=${encodeURIComponent(config)}`;
}
/** VS Code's documented install link: the whole server definition, its server ID included, as URL-encoded JSON. */
function vscodeInstallLink(target) {
    return `vscode:mcp/install?${encodeURIComponent(JSON.stringify({ name: target.cliName, type: "http", url: target.url }))}`;
}
/**
 * The step every guide reaches: Privacy Bee's own sign-in and consent screen,
 * after whatever the AI's app does to open it (`lead`).
 */
function signIn(target, lead = []) {
    const sentence = `sign in with your ${target.account} and approve the connection.`;
    return lead.length === 0
        ? { text: [sentence.charAt(0).toUpperCase() + sentence.slice(1)] }
        : { text: [...lead, ` ${sentence}`] };
}
/**
 * Every AI with a guide, in the order the tabs read. Each one's steps are
 * checked against its vendors' documentation, linked under them.
 */
export const AI_CLIENTS = [
    {
        id: "claude",
        label: "Claude",
        docs: [
            {
                href: "https://support.claude.com/en/articles/11175166-get-started-with-custom-connectors-using-remote-mcp",
                label: "Claude's guide to custom connectors",
            },
        ],
        steps: (target) => [
            {
                text: [
                    "Open ",
                    { ui: "Customize → Connectors" },
                    ", select ",
                    { ui: "+ Add" },
                    " and then ",
                    { ui: "Add custom connector" },
                    ". On Claude Team or Enterprise, an Owner adds it once under ",
                    { ui: "Organization settings → Connectors → Add → Custom → Web" },
                    ", and you select ",
                    { ui: "Connect" },
                    " on it here instead.",
                ],
            },
            {
                text: [
                    "Name it ",
                    { literal: target.name },
                    ", paste the server URL and select ",
                    { ui: "Continue" },
                    " twice. Keep ",
                    { ui: "Use Claude's published identity" },
                    " as the OAuth client and select ",
                    { ui: "Add" },
                    ".",
                ],
            },
            signIn(target),
            { text: ["In a chat, turn it on from the ", { ui: "+" }, " menu under ", { ui: "Connectors" }, "."] },
        ],
    },
    {
        id: "claude-code",
        label: "Claude Code",
        docs: [{ href: "https://code.claude.com/docs/en/mcp", label: "Claude Code's guide to MCP servers" }],
        steps: (target) => [
            {
                text: ["Add it from a terminal, for every project:"],
                copy: {
                    value: `claude mcp add --transport http --scope user ${target.cliName} ${target.url}`,
                    label: "Copy the command",
                    block: false,
                },
            },
            {
                text: ["Start Claude Code, run ", { literal: "/mcp" }, ", pick ", { ui: target.cliName }, " and choose ", { ui: "Authenticate" }, "."],
            },
            signIn(target, ["In the browser that opens,"]),
        ],
    },
    {
        id: "chatgpt",
        label: "ChatGPT",
        docs: [{ href: "https://developers.openai.com/api/docs/guides/custom-mcp-server", label: "OpenAI's guide to custom MCP servers" }],
        steps: (target) => [
            {
                text: [
                    "Open ",
                    { ui: "Plugins" },
                    " (chatgpt.com/plugins), select ",
                    { ui: "+" },
                    " and then ",
                    { ui: "Add custom MCP server" },
                    ". On a Business, Enterprise or Edu workspace, your role may need the ",
                    { ui: "Create plugins with MCPs" },
                    " permission.",
                ],
            },
            {
                text: [
                    "Name it ",
                    { literal: target.name },
                    ", paste the server URL as the ",
                    { ui: "Server URL" },
                    ", choose ",
                    { ui: "OAuth" },
                    ", confirm the risk notice and select ",
                    { ui: "Create as a plugin" },
                    ".",
                ],
            },
            signIn(target, ["Install it from your plugins,"]),
            { text: ["Switch to ", { ui: "Work" }, ", start a chat and pick it with ", { ui: "@" }, "."] },
        ],
    },
    {
        id: "codex",
        label: "Codex",
        docs: [{ href: "https://learn.chatgpt.com/docs/extend/mcp?surface=cli", label: "OpenAI's guide to MCP in Codex" }],
        steps: (target) => [
            {
                text: ["Add it from a terminal. The Codex IDE extension reads the same configuration:"],
                copy: { value: `codex mcp add ${target.cliName} --url ${target.url}`, label: "Copy the command", block: false },
            },
            signIn(target, ["In the browser Codex opens,"]),
            { text: ["If Codex says to log in instead, run ", { literal: `codex mcp login ${target.cliName}` }, "."] },
        ],
    },
    {
        id: "cursor",
        label: "Cursor",
        docs: [{ href: "https://cursor.com/docs/mcp", label: "Cursor's guide to MCP servers" }],
        steps: (target) => [
            {
                text: ["Install it in Cursor, or add it to ", { literal: "~/.cursor/mcp.json" }, " yourself:"],
                install: { href: cursorInstallLink(target), label: "Add to Cursor" },
                copy: { value: configEntry("mcpServers", target, { url: target.url }), label: "Copy the mcp.json entry", block: true },
            },
            signIn(target, ["When Cursor prompts you,"]),
        ],
    },
    {
        id: "vscode",
        label: "VS Code / GitHub Copilot",
        docs: [
            { href: "https://code.visualstudio.com/docs/agent-customization/mcp-servers", label: "VS Code's guide to MCP servers" },
            {
                href: "https://docs.github.com/en/copilot/how-tos/copilot-cli/customize-copilot/add-mcp-servers",
                label: "GitHub's guide to MCP servers in Copilot CLI",
            },
        ],
        steps: (target) => [
            {
                text: [
                    "Install it in VS Code, or run ",
                    { ui: "MCP: Add Server" },
                    " from the Command Palette, choose ",
                    { ui: "HTTP" },
                    ", paste the server URL and enter ",
                    { literal: target.cliName },
                    " as the server ID. On Copilot Business or Enterprise, an admin has to allow MCP servers first.",
                ],
                install: { href: vscodeInstallLink(target), label: "Add to VS Code" },
            },
            signIn(target, ["When VS Code asks, select ", { ui: "Allow" }, ", then"]),
            {
                text: [
                    "In GitHub Copilot CLI (1.0.83 or later), add it with this command, then run ",
                    { literal: `/mcp auth ${target.cliName}` },
                    " and sign in the same way:",
                ],
                copy: { value: `copilot mcp add --transport http ${target.cliName} ${target.url}`, label: "Copy the command", block: false },
            },
        ],
    },
    {
        id: "grok",
        label: "Grok",
        docs: [{ href: "https://docs.x.ai/grok/connectors", label: "xAI's guide to Grok connectors" }],
        steps: (target) => [
            {
                text: [
                    "Open grok.com/connectors, select ",
                    { ui: "New Connector" },
                    " and then ",
                    { ui: "Custom" },
                    ", and paste the server URL. On Grok Business or Enterprise, an admin adds it first under ",
                    { ui: "Grok Business → Connectors" },
                    " at console.x.ai.",
                ],
            },
            signIn(target, ["Then"]),
        ],
    },
    {
        id: "antigravity",
        label: "Google Antigravity (Gemini)",
        docs: [{ href: "https://antigravity.google/docs/mcp", label: "Antigravity's guide to MCP servers" }],
        steps: (target) => [
            {
                text: [
                    "In the agent panel, open ",
                    { ui: "… → MCP Servers → Manage MCP Servers → View raw config" },
                    " and add this to ",
                    { literal: "mcp_config.json" },
                    ":",
                ],
                copy: { value: configEntry("mcpServers", target, { serverUrl: target.url }), label: "Copy the mcp_config.json entry", block: true },
            },
            {
                text: [
                    "Open Agent settings (",
                    { ui: "Cmd + ," },
                    "), go to ",
                    { ui: "Customizations" },
                    " and select ",
                    { ui: "Authenticate" },
                    " next to ",
                    { literal: target.cliName },
                    ".",
                ],
            },
            signIn(target, ["In the browser,"]),
            { text: ["Paste the code the browser shows into the settings panel and select ", { ui: "Submit" }, "."] },
        ],
    },
    {
        id: "other",
        label: "Other MCP clients",
        docs: [
            { href: "https://zed.dev/docs/ai/mcp", label: "Zed's guide to MCP servers" },
            { href: "https://goose-docs.ai/docs/getting-started/using-extensions/", label: "goose's guide to extensions" },
        ],
        steps: (target) => [
            {
                text: [
                    { ui: "Zed:" },
                    " open ",
                    { ui: "Settings → AI → MCP Servers" },
                    ", select ",
                    { ui: "Add Server" },
                    " and then ",
                    { ui: "Add Remote Server" },
                    ", or add this to ",
                    { literal: "settings.json" },
                    ". Then select ",
                    { ui: "Authenticate" },
                    ".",
                ],
                copy: { value: configEntry("context_servers", target, { url: target.url }), label: "Copy the settings.json entry", block: true },
            },
            {
                text: [
                    { ui: "goose:" },
                    " open ",
                    { ui: "Extensions → Add custom extension" },
                    ", name it ",
                    { literal: target.name },
                    ", choose ",
                    { ui: "Streamable HTTP" },
                    " and paste the server URL as the ",
                    { ui: "Endpoint" },
                    ".",
                ],
            },
            signIn(target, ["When either one opens your browser,"]),
        ],
    },
];
/**
 * The guide to every AI for one server at the address the caller's
 * environment serves it on: the one way either surface reads this package.
 * Built fresh on every call, so the array is the caller's own.
 */
export function connectorGuides(server, url) {
    const target = { ...server, url };
    return AI_CLIENTS.map((client) => ({
        id: client.id,
        label: client.label,
        steps: client.steps(target),
        docs: client.docs,
    }));
}
